import { cn } from '@creator/ui/utils/ui';
import JoinButtonModal from '@src/components/JoinButtonModal/JoinButtonModal';
import { ModalContext } from '@src/context/modal-context';
import type { UseModal } from '@src/hooks/use-modal';
import { ensureTokenUrl } from '@src/model/config/helpers';
import { useStoreActions, useStoreState } from '@src/model/hooks';
import { ensurePostUrl, isPostDeleted } from '@src/model/upvote/helpers';
import { getUserId, isUserJoinedToToken } from '@src/model/user/helpers';
import { usePost } from '@src/store/posts/hooks/usePost';
import { getUrlPath } from '@src/utils/url-utils/url-utils';
import type { FC } from 'react';
import { useContext, useState } from 'react';
import { useNavigate } from 'react-router';
import PollOptions from '../PollOptions/PollOptions';

export interface PollOption {
    label: string;
    value: string;
    isLoading: boolean;
}

export interface PollProps {
    className?: string;
    tokenName: string;
    postId: string;
    isPin?: boolean;
}

const Poll: FC<PollProps> = props => {
    const { postId, tokenName, className, isPin = false } = props;

    const vote = useStoreActions(actions => actions.voting.vote);
    const post = usePost(postId);
    const [whatVoting, setWhatVoting] = useState<number>();
    const { handleModal } = useContext<UseModal>(ModalContext);
    const isLoggedIn = useStoreState(state => state.user.isLoggedIn);
    const navigate = useNavigate();
    async function onOptionClick(i: number): Promise<void> {
        if (!isLoggedIn) return navigate(`/bbs/login?next=${getUrlPath()}`);
        if (isPin) return navigate(`/${ensureTokenUrl(tokenName)}/${ensurePostUrl(postId)}`);
        if (!isUserJoinedToToken(tokenName)) return handleModal(<JoinButtonModal tokenName={tokenName} />);
        if (isPostDeleted(postId)) return;

        if (whatVoting) return;

        setWhatVoting(i);

        try {
            await vote({
                userId: getUserId() || '',
                postId: postId || '',
                selectedOptionIndex: i,
            });

            setWhatVoting(undefined);
        } catch (error) {
            setWhatVoting(undefined);
        }
    }

    function renderTitle() {
        if (!post || !post.pollTitle) return null;
        return <h3 className="text-base font-bold">{post.pollTitle}</h3>;
    }

    return (
        <div className={cn(className, 'space-y-2')}>
            {renderTitle()}
            <PollOptions isVoting={whatVoting} tokenName={tokenName} onOptionClick={onOptionClick} postId={postId} isPin={isPin} />
        </div>
    );
};

export default Poll;
